// 隣接シナジー。v0.6.0 の追加システム。
//
// 上下左右に隣接するデコの組み合わせでボーナス属性が発動する。gardenAttributes に
// 統合され、来店条件の「あと一押し」になる。発動中のペアは描画でキラキラの連結線、
// ツールチップに一行で示す。
//
// 設計（決定性）: 庭の静的な配置（と、しおれ・成長などの条件付き）だけで決まる純関数。
// 乱数・時刻に依存しない（条件付きルールは呼び出し側が満たし状況を渡す）。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。

import type { AttributeKey } from './decos';
import type { GardenGrid } from './garden';
import { GRID_SIZE, cellIndex } from './garden';
import { isPlantDeco, stageForGrowSec, GROWTH_STAGES } from './plants';
import type { Planting } from './plants';

/** シナジールール 1 件。 */
export interface SynergyRule {
  /** 安定 ID（図鑑・セーブ非依存だが UI のキーに使う）。 */
  id: string;
  /** 表示名。 */
  name: string;
  /** 構成する 2 つのデコ ID（順不同でマッチ）。同じ ID 同士のペアも可。 */
  pair: readonly [string, string];
  /** 発動で加算される属性。 */
  attribute: AttributeKey;
  /** 加算量。 */
  amount: number;
  /** 1 ペアあたりの上限ではなく、ルール全体の発動回数上限（連鎖の暴走を防ぐ）。 */
  maxStacks: number;
  /** フレーバー（ステータス画面のルール一覧で出す）。 */
  flavor: string;
  /**
   * 追加の発動条件（任意）。隣接していても条件を満たさないと不発（例: 花壇×キノコは
   * キノコがしおれていると香りが出ない）。条件はセル context を受けて判定する。
   */
  condition?: (ctx: PairContext) => boolean;
  /** 成長倍率（任意）。池×若木のような「成長を早める」効果のフラグ。属性ではなく
   *  別途 simulate が拾う（ここでは表示と一覧のために持つ）。 */
  growthBoost?: number;
}

/** ペア判定時に condition へ渡すセル文脈。 */
export interface PairContext {
  /** ペアの一方のセル index。 */
  aIndex: number;
  /** ペアの他方のセル index。 */
  bIndex: number;
  /** セル index → そのセルがしおれているか（ゆらぎデコのみ true になり得る）。 */
  wilted: (index: number) => boolean;
  /** セル index → 植物の成長段階インデックス（0..3、非植物は -1）。 */
  plantStage: (index: number) => number;
}

/**
 * シナジールール（6 種）。GAME_DESIGN.md / 要望の例に沿う。
 *  1. ランタン×ベンチ: 静寂+1（灯りの下の読書席）
 *  2. 池×若木(成木以上): 木の緑+1 ＆ 成長 1.2 倍
 *  3. 季節の結晶×季節の結晶（同種でなくても結晶同士）: 共鳴で結晶+1（上限つき）
 *  4. 花壇×キノコ: 香り+1（キノコがしおれていると不発）
 *  5. 噴水×石畳: にぎやか+1（広場）
 *  6. クリスタル群晶×ランタン: 光+1（灯りに結晶がきらめく）
 */
export const SYNERGY_RULES: readonly SynergyRule[] = [
  {
    id: 'lantern_bench',
    name: '灯りの読書席',
    pair: ['lantern', 'bench'],
    attribute: 'calm',
    amount: 1,
    maxStacks: 4,
    flavor: 'ランタンの灯りの下にベンチ。静かに本を読みたくなる席に。',
  },
  {
    id: 'pond_sapling',
    name: '水辺の若木',
    pair: ['pond', 'sapling'],
    attribute: 'green',
    amount: 1,
    maxStacks: 4,
    flavor: '池のほとりの木はよく育つ。緑が増し、成長も少し早まる。',
    // 若木（sapling 段階）以上で発動。芽のうちは不発。
    condition: (ctx) => {
      // sapling 側のセルを探す（pair の並びに依存せず、植物側が sapling 以上か）。
      const aStage = ctx.plantStage(ctx.aIndex);
      const bStage = ctx.plantStage(ctx.bIndex);
      const treeStage = Math.max(aStage, bStage);
      return treeStage >= GROWTH_STAGES.indexOf('sapling');
    },
    growthBoost: 1.2,
  },
  {
    id: 'crystal_resonance',
    name: '結晶の共鳴',
    pair: ['crystal_cluster', 'crystal_cluster'],
    attribute: 'crystal',
    amount: 1,
    maxStacks: 3, // 連鎖の上限（暴走防止）
    flavor: 'クリスタル同士が隣り合うと共鳴して結晶の気配が強まる。',
  },
  {
    id: 'bed_mushroom',
    name: '香りのきのこ畑',
    pair: ['violet_bed', 'glow_mushroom'],
    attribute: 'scent',
    amount: 1,
    maxStacks: 4,
    flavor: '花壇のそばの光るキノコが香りを立てる。ただししおれていると香らない。',
    // キノコがしおれていると不発。
    condition: (ctx) => !ctx.wilted(ctx.aIndex) && !ctx.wilted(ctx.bIndex),
  },
  {
    id: 'fountain_plaza',
    name: 'にぎわいの広場',
    pair: ['fountain', 'stone_tile'],
    attribute: 'lively',
    amount: 1,
    maxStacks: 4,
    flavor: '噴水を石畳が囲むと、人の集まる広場のにぎやかさが生まれる。',
  },
  {
    id: 'lantern_crystal',
    name: 'きらめく灯り',
    pair: ['lantern', 'crystal_cluster'],
    attribute: 'light',
    amount: 1,
    maxStacks: 4,
    flavor: 'ランタンの灯りがクリスタルに反射して、ひときわ明るく輝く。',
  },
];

/** ペアが (a,b) または (b,a) で一致するか。 */
function pairMatches(rule: SynergyRule, d1: string, d2: string): boolean {
  const [p, q] = rule.pair;
  return (d1 === p && d2 === q) || (d1 === q && d2 === p);
}

/** 発動した 1 件のシナジー（描画の連結線・属性合算に使う）。 */
export interface ActiveSynergy {
  ruleId: string;
  name: string;
  attribute: AttributeKey;
  amount: number;
  /** 連結する 2 セル index（描画でキラキラ線を引く）。 */
  a: number;
  b: number;
  /** 成長ブースト倍率（無ければ 1.0）。 */
  growthBoost: number;
}

/** シナジー評価の入力。庭と、しおれ・成長段階の参照を渡す。 */
export interface SynergyInput {
  garden: GardenGrid;
  /** セル index → しおれているか（ゆらぎ評価ずみ）。省略時は常に false。 */
  wilted?: (index: number) => boolean;
  /** セル index → 植物の成長段階インデックス（0..3、非植物 -1）。省略時は plantings から導出。 */
  plantStage?: (index: number) => number;
  /** plantings（plantStage 未指定時の導出に使う）。 */
  plantings?: Record<string, Planting>;
}

/**
 * 隣接する上下左右ペアを走査して発動中のシナジーを列挙する純関数。
 *
 * 各ルールは maxStacks までしか積まない（連鎖の暴走を防ぐ）。重複カウントを避けるため
 * ペアは「右隣・下隣」だけ見る（各無向ペアを一度だけ訪れる）。
 */
export function activeSynergies(input: SynergyInput): ActiveSynergy[] {
  const { garden } = input;
  const wilted = input.wilted ?? (() => false);
  const plantStage =
    input.plantStage ??
    ((index: number): number => {
      const cell = garden[index];
      if (!cell || !isPlantDeco(cell.decoId)) return -1;
      const p = input.plantings?.[String(index)];
      const growSec = p ? p.growSec : 0;
      return GROWTH_STAGES.indexOf(stageForGrowSec(growSec));
    });

  const out: ActiveSynergy[] = [];
  const stackCount = new Map<string, number>();

  function consider(aIdx: number, bIdx: number): void {
    const a = garden[aIdx];
    const b = garden[bIdx];
    if (!a || !b) return;
    for (const rule of SYNERGY_RULES) {
      if (!pairMatches(rule, a.decoId, b.decoId)) continue;
      const used = stackCount.get(rule.id) ?? 0;
      if (used >= rule.maxStacks) continue;
      if (rule.condition) {
        const ok = rule.condition({ aIndex: aIdx, bIndex: bIdx, wilted, plantStage });
        if (!ok) continue;
      }
      stackCount.set(rule.id, used + 1);
      out.push({
        ruleId: rule.id,
        name: rule.name,
        attribute: rule.attribute,
        amount: rule.amount,
        a: aIdx,
        b: bIdx,
        growthBoost: rule.growthBoost ?? 1.0,
      });
    }
  }

  for (let gy = 0; gy < GRID_SIZE; gy++) {
    for (let gx = 0; gx < GRID_SIZE; gx++) {
      const idx = cellIndex(gx, gy);
      if (garden[idx] == null) continue;
      // 右隣
      if (gx + 1 < GRID_SIZE) consider(idx, cellIndex(gx + 1, gy));
      // 下隣
      if (gy + 1 < GRID_SIZE) consider(idx, cellIndex(gx, gy + 1));
    }
  }
  return out;
}

/** 発動中シナジーの属性合算（gardenAttributes が足し込む用）。 */
export function synergyAttributeTotals(
  synergies: ActiveSynergy[],
): Partial<Record<AttributeKey, number>> {
  const out: Partial<Record<AttributeKey, number>> = {};
  for (const s of synergies) {
    out[s.attribute] = (out[s.attribute] ?? 0) + s.amount;
  }
  return out;
}

/**
 * セル index → そのセルにかかる最大の成長ブースト倍率（複数シナジーがあれば最大を採用）。
 * simulate が植物の成長速度に掛けるのに使う。
 */
export function growthBoostByCell(synergies: ActiveSynergy[]): Map<number, number> {
  const out = new Map<number, number>();
  for (const s of synergies) {
    if (s.growthBoost <= 1.0) continue;
    for (const idx of [s.a, s.b]) {
      const prev = out.get(idx) ?? 1.0;
      if (s.growthBoost > prev) out.set(idx, s.growthBoost);
    }
  }
  return out;
}
